import {
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
} from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import * as React from "react";
import { Menu, MenuItem } from "react-aria-menubutton";
import { useTranslation } from "react-i18next";

import ManageUserGroupsForm, { FormValues } from "./manage-user-group/ManageUserGroupsForm";
import style from "./user-groups.scss";
import DeleteConfirmationDialog from "components/confirmation/DeleteConfirmationDialog";
import Delete from "components/icons/Delete";
import Edit from "components/icons/Edit";
import KebabMenu from "components/kebab-menu/KebabMenu";
import Modal from "components/modal/Modal";
import TextWithTooltip from "components/table/TextWithTooltip";
import { AUTH_USER_GROUP_DELETE, AUTH_USER_GROUP_EDIT } from "domain/authority";
import { UserGroup } from "domain/userGroups";
import { FLAG_UICOMPS_USER_GROUPS_TABLE } from "services/feature/FeatureFlagService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userGroupsService } from "services/user-groups/UserGroupsService";
import { userSessionService } from "services/user/UserSessionService";
import buttons from "styles/buttons.scss";
import { logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    groupUuid: string;
    name: string;
    onGroupDelete: () => void;
    onGroupEdit: () => void;
    groupTuple: UserGroup;
    tableRef: React.RefObject<HTMLElement>;
}

interface Result {
    title: string;
    message: string;
}

const UserGroupsKebabMenu = (props: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    //TODO BCC-6868 Can be removed after User groups table rewrite is done
    const isUserGroupsTableFeatureEnabled = useFeature(FLAG_UICOMPS_USER_GROUPS_TABLE);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const [resultVisible, setResultVisible] = React.useState<boolean>(false);
    const [deleteGroupFormVisible, setDeleteGroupFormVisible] = React.useState<boolean>(false);
    const [okClicked, setOkClicked] = React.useState<boolean>(false);
    const [editGroupFormVisible, setEditGroupFormVisible] = React.useState<boolean>(false);

    React.useEffect(() => {
        return () => abortControllers.filter((value) => !value.signal.aborted).forEach((value) => value.abort());
    }, []);

    const showResult = (resultToShow: Result) => {
        setResult(resultToShow);
        setResultVisible(true);
    };

    const hideResultAndRedirectToRoot = () => {
        setResultVisible(false);
        props.onGroupDelete();
    };

    const handleGroupDelete = () => {
        deleteGroupSubmitEventHandler();
        setOkClicked(true);
    };

    const editUserGroupSubmitEventHandler = async ({
        groupName,
        backgroundColor,
        roleUuid,
        textColor,
        usersList,
    }: FormValues): Promise<void> => {
        const abortController = new AbortController();
        const editRequest = {
            uuid: props.groupTuple.uuid,
            name: groupName,
            backgroundColor: backgroundColor,
            textColor: textColor,
            abortController: abortController,
            roleUuid: roleUuid,
            usersList: usersList,
        };
        abortControllers.push(abortController);
        const { signal } = abortController;
        try {
            await userGroupsService.editGroup(editRequest);
        } catch {
            if (!signal.aborted) {
                setEditGroupFormVisible(false);
                showResult({
                    title: t("UserGroups.manageUserGroupsView.editUserGroup.editGroupFailure.title"),
                    message: t("UserGroups.manageUserGroupsView.editUserGroup.editGroupFailure.message"),
                });
            }
            return;
        }
        if (signal.aborted) {
            return;
        }
        setEditGroupFormVisible(false);
        showResult({
            title: t("UserGroups.manageUserGroupsView.editUserGroup.resultTitle"),
            message: t("UserGroups.manageUserGroupsView.editUserGroup.resultMessage", { groupName: groupName }),
        });
    };
    const deleteGroupSubmitEventHandler = async () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const { signal } = abortController;
        try {
            await userGroupsService.deleteUserGroup(props.groupUuid, abortController);
        } catch (e) {
            if (!signal.aborted) {
                setOkClicked(false);
                setDeleteGroupFormVisible(false);
                showResult({
                    title: t("UserGroups.manageUserGroupsView.deleteUserGroup.groupDeleted.groupNotDeletedTitle"),
                    message: t("UserGroups.manageUserGroupsView.deleteUserGroup.groupDeleted.failureMessage"),
                });
                logger.error("Failed to delete a user group", e);
            }
            return;
        }
        if (signal.aborted) {
            return;
        }
        setOkClicked(false);
        setDeleteGroupFormVisible(false);
        showResult({
            title: t("UserGroups.manageUserGroupsView.deleteUserGroup.groupDeleted.groupDeletedTitle"),
            message: t("UserGroups.manageUserGroupsView.deleteUserGroup.groupDeleted.successMessage", {
                name: props.name,
            }),
        });
    };

    return (
        //TODO BCC-686 Remove the conditional after users group table rewrite is complete
        <>
            {isUserGroupsTableFeatureEnabled ? (
                <DropdownMenu
                    position={"bottom-start"}
                    dropdownOffset={"Xxs"}
                    closeOnSelection
                    flipOptions={{
                        fallbackPlacements: ["top-start"],
                    }}
                    testId={testIds.common.primaryView.table.kebabMenu.itself}
                >
                    <DropdownTrigger>
                        <IconButton>
                            <Kebab />
                        </IconButton>
                    </DropdownTrigger>
                    <DropdownContent containerRef={props.tableRef}>
                        {userSessionService.userHasAllAuthorities([AUTH_USER_GROUP_EDIT]) && (
                            <DropdownMenuItem
                                testId={testIds.workArea.userGroup.table.kebabMenu.editButton}
                                onClick={() => {
                                    setEditGroupFormVisible(true);
                                }}
                            >
                                {t("Common.edit")}
                            </DropdownMenuItem>
                        )}
                        {userSessionService.userHasAllAuthorities([AUTH_USER_GROUP_DELETE]) && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => {
                                        setDeleteGroupFormVisible(true);
                                        usageStatisticsService.sendEvent({
                                            category: Category.USER_GROUP,
                                            action: Action.REMOVE_USER_GROUP,
                                        });
                                    }}
                                    testId={testIds.workArea.userGroup.table.kebabMenu.deleteButton}
                                    errorStyle
                                >
                                    {t("Common.delete")}
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownContent>
                </DropdownMenu>
            ) : (
                //TODO BCC-686 Remove this conditional after users group table rewrite is complete. Check all relevant styles and remove any that become redundant
                <KebabMenu>
                    <div className={style.deleteIcon}>
                        <Menu
                            className={style.kebabMenu}
                            data-testid={testIds.common.primaryView.table.kebabMenu.itself}
                        >
                            <ul>
                                {userSessionService.userHasAllAuthorities([AUTH_USER_GROUP_DELETE]) && (
                                    <li>
                                        <TextWithTooltip text={t("Common.delete")}>
                                            <MenuItem
                                                tag="button"
                                                onKeyDown={undefined}
                                                onClick={() => {
                                                    setDeleteGroupFormVisible(true);
                                                    usageStatisticsService.sendEvent({
                                                        category: Category.USER_GROUP,
                                                        action: Action.REMOVE_USER_GROUP,
                                                    });
                                                }}
                                                data-testid={testIds.workArea.userGroup.table.kebabMenu.deleteButton}
                                            >
                                                <Delete color={theme.iconFillColor} />
                                            </MenuItem>
                                        </TextWithTooltip>
                                    </li>
                                )}

                                {userSessionService.userHasAllAuthorities([AUTH_USER_GROUP_EDIT]) && (
                                    <li>
                                        <TextWithTooltip text={t("Common.edit")}>
                                            <MenuItem
                                                tag="button"
                                                onKeyDown={undefined}
                                                onClick={() => {
                                                    setEditGroupFormVisible(true);
                                                }}
                                                data-testid={testIds.workArea.userGroup.table.kebabMenu.editButton}
                                            >
                                                <Edit color={theme.iconFillColor} />
                                            </MenuItem>
                                        </TextWithTooltip>
                                    </li>
                                )}
                            </ul>
                        </Menu>
                    </div>
                </KebabMenu>
            )}
            <Modal
                isOpen={editGroupFormVisible}
                hideModal={() => setEditGroupFormVisible(false)}
                modalTitle={t("UserGroups.manageUserGroupsView.editUserGroup.title")}
            >
                <ManageUserGroupsForm
                    submitEventHandler={editUserGroupSubmitEventHandler}
                    edit={true}
                    groupTuple={props.groupTuple}
                    setModalFormVisible={setEditGroupFormVisible}
                />
            </Modal>
            <DeleteConfirmationDialog
                modalDisplayed={deleteGroupFormVisible}
                introductionMessage={
                    <div className={style.resultContainer}>
                        {t("UserGroups.manageUserGroupsView.deleteUserGroup.introductionMessage", {
                            name: props.name,
                        })}
                    </div>
                }
                confirmationMessage={""}
                handleCommand={handleGroupDelete}
                setModalDisplayed={() => setDeleteGroupFormVisible(false)}
                title={t("UserGroups.manageUserGroupsView.deleteUserGroup.title")}
                loading={okClicked}
            />
            <Modal isOpen={resultVisible} hideModal={hideResultAndRedirectToRoot} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                <div className={style.okButtonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResultAndRedirectToRoot}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default UserGroupsKebabMenu;
